import React from 'react';
import { Helmet } from 'react-helmet-async';

const BRAND = 'DUSKPROOF';
const DEFAULT_DESCRIPTION =
  'Institution-grade validator infrastructure for reliable passive yield — staking, monitoring tools, and blockchain ops from DUSKPROOF.';
const DEFAULT_OG_PATH = '/images/logo.png';

function absoluteUrl(pathOrUrl: string, origin: string): string {
  if (!pathOrUrl) return origin;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${origin}${path}`;
}

function cleanPath(pathname: string): string {
  const noQuery = pathname.split('?')[0].split('#')[0];
  if (noQuery.length > 1 && noQuery.endsWith('/')) return noQuery.slice(0, -1);
  return noQuery || '/';
}

export type SeoHeadProps = {
  title: string;
  description?: string;
  /** Path only, e.g. /blog/slug — query/utm stripped for canonical */
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

/** Per-page head tags for public SEO. Safe client-side via react-helmet-async. */
export function SeoHead({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  image = DEFAULT_OG_PATH,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoHeadProps) {
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://duskproof.com';

  const pathname =
    path !== undefined
      ? cleanPath(path)
      : typeof window !== 'undefined'
        ? cleanPath(window.location.pathname)
        : '/';

  const canonical = absoluteUrl(pathname === '/' ? '/' : pathname, origin);
  const ogImage = absoluteUrl(image, origin);
  const fullTitle = title.includes(BRAND) ? title : `${title} | ${BRAND}`;
  const desc = (description || DEFAULT_DESCRIPTION).slice(0, 160);
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';

  const graph = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={BRAND} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      {graph.map((node, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
}

export function organizationJsonLd(origin = 'https://duskproof.com') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND,
    url: origin,
    logo: `${origin}/images/logo.png`,
    email: 'office@duskproof.com',
    sameAs: ['https://x.com/duskproof', 'https://github.com/duskproof'],
  };
}

export function websiteJsonLd(origin = 'https://duskproof.com') {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: BRAND,
    url: origin,
  };
}

export function breadcrumbJsonLd(
  items: { name: string; path: string }[],
  origin = 'https://duskproof.com',
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path, origin),
    })),
  };
}

export default SeoHead;
